import naja from 'naja';

export default class CubeeForm {
    static currentStep: number = 1;
    static totalSteps: number = 5;
    static formData: { [key: string]: any } = {};

    static init(): void {
        const calculator = document.getElementById('cubee-calculator');
        if (!calculator) return;

        document.querySelectorAll('.next').forEach(button => {
            button.addEventListener('click', () => this.nextStep());
        });

        document.querySelectorAll('.prev').forEach(button => {
            button.addEventListener('click', () => this.prevStep());
        });

        document.querySelector('.reset')?.addEventListener('click', () => this.resetForm());

        document.querySelectorAll('input').forEach(input => {
            input.addEventListener('input', (e) => this.handleInput(e as InputEvent));
        });

        this.updateNavigation();
    }

    static nextStep(): void {
        if (this.validateCurrentStep()) {
            if (this.currentStep < this.totalSteps) {
                this.hideStep(this.currentStep);
                this.currentStep++;
                this.showStep(this.currentStep);
                this.updateNavigation();
            }
        }
    }

    static prevStep(): void {
        if (this.currentStep > 1) {
            this.hideStep(this.currentStep);
            this.currentStep--;
            this.showStep(this.currentStep);
            this.updateNavigation();
        }
    }

    static hideStep(step: number): void {
        const stepElement = document.getElementById(`step-${step}`);
        stepElement?.classList.add('hide');
    }

    static showStep(step: number): void {
        const stepElement = document.getElementById(`step-${step}`);
        stepElement?.classList.remove('hide');

        if (stepElement?.hasAttribute('data-cubee-finish')) {
            this.calculate();
        }
    }

    static updateConditions(): void {
        const currentStepElement = document.getElementById(`step-${this.currentStep}`);
        if (!currentStepElement) return;

        const conditionInputs = currentStepElement.querySelectorAll('input[data-condition]') as NodeListOf<HTMLInputElement>;
        conditionInputs.forEach(input => {
            const conditionName = input.dataset.condition;
            if (!conditionName) return;

            const conditionElements = currentStepElement.querySelectorAll(`[name="${conditionName}"]`) as NodeListOf<HTMLInputElement>;
            let shouldEnable = false;

            conditionElements.forEach(conditionElement => {
                if (conditionElement.type === 'radio' || conditionElement.type === 'checkbox') {
                    if (conditionElement.checked) {
                        if (input.dataset.conditionNegativeValue) {
                            shouldEnable = conditionElement.value !== input.dataset.conditionNegativeValue;
                        } else if (input.dataset.conditionValue) {
                            shouldEnable = conditionElement.value === input.dataset.conditionValue;
                        }
                    }
                } else {
                    if (input.dataset.conditionNegativeValue) {
                        shouldEnable = conditionElement.value !== input.dataset.conditionNegativeValue;
                    } else if (input.dataset.conditionValue) {
                        shouldEnable = conditionElement.value === input.dataset.conditionValue;
                    }
                }
            });

            input.disabled = !shouldEnable;

            if (input.disabled) {
                input.value = '';
                input.closest('.step-form__form-row')?.classList.remove('step-form__form-row--error');
            }
        });
    }

    static validateCurrentStep(): boolean {
        const currentStepElement = document.getElementById(`step-${this.currentStep}`);
        if (!currentStepElement) return false;

        const inputs = currentStepElement.querySelectorAll('input[required]:not([disabled])') as NodeListOf<HTMLInputElement>;

        let isValid = true;

        inputs.forEach(input => {
            const inputElement = input as HTMLInputElement;
            const row = inputElement.closest('.step-form__form-row');

            if (inputElement.type === 'radio') {
                const name = inputElement.name;
                const checkedRadio = currentStepElement.querySelector(`input[name="${name}"]:checked`);
                if (!checkedRadio) {
                    row?.classList.add('step-form__form-row--error');
                    isValid = false;
                } else {
                    row?.classList.remove('step-form__form-row--error');
                }
            } else {
                if (!inputElement.value.trim()) {
                    row?.classList.add('step-form__form-row--error');
                    isValid = false;
                } else {
                    row?.classList.remove('step-form__form-row--error');
                }
            }
        });

        return isValid;
    }

    static handleInput(event: InputEvent): void {
        const input = event.target as HTMLInputElement;
        const row = input.closest('.step-form__form-row');

        if (input.type === 'radio') {
            const name = input.name;
            const radioGroup = document.querySelectorAll(`input[name="${name}"]`);
            radioGroup.forEach(radio => {
                radio.closest('.step-form__form-row')?.classList.remove('step-form__form-row--error');
            });
        } else {
            if (input.required && !input.value.trim()) {
                row?.classList.add('step-form__form-row--error');
            } else {
                row?.classList.remove('step-form__form-row--error');
            }
        }

        this.updateConditions();
        this.updateFormData(input);
    }

    static updateFormData(input: HTMLInputElement): void {
        this.formData[input.name] = input.type === 'checkbox' ? input.checked : input.value;
    }

    static resetForm(): void {
        this.formData = {};
        document.querySelectorAll('input').forEach(input => {
            const inputElement = input as HTMLInputElement;
            if (inputElement.type === 'radio' || inputElement.type === 'checkbox') {
                inputElement.checked = false;
            } else {
                inputElement.value = '';
            }
        });
        document.querySelectorAll('.step-form__form-row--error').forEach(row => {
            row.classList.remove('step-form__form-row--error');
        });
        this.currentStep = 1;
        for (let i = 1; i <= this.totalSteps; i++) {
            if (i === 1) {
                this.showStep(i);
            } else {
                this.hideStep(i);
            }
        }
        this.updateNavigation();
    }

    static updateNavigation(): void {
        const steps = document.querySelectorAll('.steps li');
        steps.forEach((step, index) => {
            const dot = step.querySelector('.dot');
            if (index + 1 < this.currentStep) {
                dot?.classList.add('completed');
                dot?.classList.remove('selected');
            } else if (index + 1 === this.currentStep) {
                dot?.classList.add('selected');
                dot?.classList.remove('completed');
            } else {
                dot?.classList.remove('completed', 'selected');
            }
        });
    }

    static calculate(): void {
        const data = this.formData;

        const calculator = document.getElementById('cubee-calculator');
        const url = calculator?.dataset.action;
        if (!url) return;

        const loader = calculator?.querySelector('.step-form__loader');
        loader?.classList.add('step-form__loader--visible');

        naja.makeRequest('POST', url, JSON.stringify(data), {
            history: false,
            headers: {
                'Content-Type': 'application/json'
            }
        }).then((response) => {
            loader?.classList.remove('step-form__loader--visible');
            if (response.status === 'success') {
                this.showResults(response.result);
            } else {
                console.error(response);
            }
        });
    }

    static showResults(results: any): void {
        console.log(results);
        const calculator = document.getElementById('cubee-calculator');
        calculator?.classList.add('show-results');

        const resultsTable = calculator?.querySelector('#cubee-table-results') as HTMLTableElement;
        resultsTable.innerHTML = `
            <tr>
                <th>Termín</th>
                <th>Cena bez PV</th>
                <th>Cena s Basic PV</th>
                <th>Cena s Smart PV</th>
                <th>Úspora Smart PV</th>
            </tr>
        `;

        results.months.forEach((result: any) => {
            const row = document.createElement('tr');
            const date = new Date(result.month);
            row.innerHTML = `
                <td>${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}</td>
                <td>${formatPrice(result.priceWithoutPV)}</td>
                <td>${formatPrice(result.priceWithBasicPV)}</td>
                <td>${formatPrice(result.priceWithSmartPV)}</td>
                <td>${formatPrice(result.smartPVSavings)}</td>
            `;
            resultsTable.appendChild(row);
        });

        const totalRow = document.createElement('tr');
        totalRow.innerHTML = `
            <td>Celkem</td>
            <td>${formatPrice(results.priceWithoutPV)}</td>
            <td>${formatPrice(results.priceWithBasicPV)}</td>
            <td>${formatPrice(results.priceWithSmartPV)}</td>
            <td>${formatPrice(results.smartPVSavings)}</td>
        `;
        resultsTable.appendChild(totalRow);

        function formatPrice(price: number): string {
            return `${Math.abs(price).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")},-`;
        }
    }

}
